'use client';

import { ArrowLeft, ScrollText } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React from 'react';

import { AmbientGlow } from '@/components/landing/AmbientGlow';
import { Link } from '@/i18n/routing';

function Section({
  title,
  children,
  last,
}: {
  title: string;
  children?: React.ReactNode;
  last?: boolean;
}) {
  return (
    <section className={last ? '' : 'border-divider-soft border-b pb-[32px]'}>
      <h2 className="font-matter text-text-primary mb-[14px] text-[22px] font-medium tracking-[-0.01em] md:text-[26px]">
        {title}
      </h2>
      {children ? (
        <div className="font-matter text-text-secondary flex flex-col gap-[16px] text-[16px] leading-[26px] tracking-[-0.01em]">
          {children}
        </div>
      ) : null}
    </section>
  );
}

export default function TermsConditionsContent() {
  const t = useTranslations('Terms');

  return (
    <main id="main">
      <section className="bg-section-b relative w-full overflow-hidden px-4 pt-[140px] pb-[60px] md:px-[80px] md:pt-[180px] md:pb-[80px]">
        <AmbientGlow />
        <div className="relative z-10 mx-auto flex max-w-[820px] flex-col items-center gap-[20px] text-center">
          <div className="bg-badge-bg border-brand-primary/60 flex h-[32px] w-fit items-center gap-[8px] rounded-[100px] border px-4 shadow-[var(--shadow-brand-soft)]">
            <ScrollText size={14} className="text-brand-primary" />
            <span className="font-matter text-badge-text text-[14px] font-bold">
              {t('badge')}
            </span>
          </div>
          <h1 className="font-matter text-text-primary text-[40px] leading-[46px] font-medium tracking-[-0.035em] md:text-[60px] md:leading-[64px]">
            {t('title')}
          </h1>
          <span className="bg-surface-subtle text-text-muted font-matter inline-flex items-center rounded-full px-[14px] py-[6px] text-[13px] font-medium">
            {t('lastUpdated')}
          </span>
        </div>
      </section>

      <section className="bg-section-a w-full px-4 py-[50px] md:px-[80px] md:py-[80px]">
        <div className="mx-auto max-w-[820px]">
          <div className="border-border-soft bg-surface-card brand-card-shadow flex flex-col gap-[32px] rounded-[24px] border p-[24px] md:p-[48px]">
            <Section title={t('termsTitle')}>
              <p>{t('termsIntro')}</p>
            </Section>

            <Section title={t('useOfServicesTitle')}>
              <p>{t('useOfServicesBody')}</p>
            </Section>

            <Section title={t('acceptableUseTitle')}>
              <p>{t('acceptableUseBody')}</p>
            </Section>

            <Section title={t('dataPrivacyTitle')}>
              <p>{t('dataPrivacyBody')}</p>
            </Section>

            <Section title={t('intellectualPropertyTitle')}>
              <p>{t('intellectualPropertyBody')}</p>
            </Section>

            <Section title={t('liabilityTitle')}>
              <p>{t('liabilityBody')}</p>
            </Section>

            <Section title={t('governingLawTitle')}>
              <p>{t('governingLawBody')}</p>
            </Section>

            <Section title={t('refundTitle')} />

            <Section title={t('freeTrialTitle')}>
              <p>{t('freeTrialBody')}</p>
            </Section>

            <Section title={t('subscriptionsTitle')}>
              <p>{t('subscriptionsBody')}</p>
            </Section>

            <Section title={t('cancellationsTitle')}>
              <p>{t('cancellationsBody')}</p>
            </Section>

            <Section title={t('refundsTitle')} last>
              <p>{t('refundsBody')}</p>
            </Section>
          </div>

          <div className="mt-[32px] flex justify-center">
            <Link
              href="/"
              className="border-border-soft bg-surface-card text-text-primary hover:border-brand-primary font-matter flex h-[48px] items-center gap-2 rounded-full border px-[24px] text-[16px] font-medium transition-colors"
            >
              <ArrowLeft size={18} className="rtl:rotate-180" />
              {t('backToHome')}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
